import { useCallback, useEffect, useRef, useState } from "react";

import { useSessionStorage } from "@ui/hooks/useStorage";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type { InfoBannerTone } from "@jti/core-components/components/InfoBanner";
import { InfoBanner as CoreInfoBanner } from "@jti/core-components/components/InfoBanner";
import { getMessageBus } from "@jti/fe-message-bus";

type BannerType = "amber" | "green" | "auto";

const bannerTypeToTone: Record<BannerType, InfoBannerTone> = {
  green: "success",
  amber: "warning",
  auto: "neutral",
};

export interface InfoBannerProps {
  content: string;
  bannerType: BannerType;
  ctaLink: string;
  ctaLabel: string;
  hideCta?: boolean;
  hideCloseIcon?: boolean;
  showInfoBanner?: boolean;
  storageKey?: string;
  dataTestId?: string;
  className?: string;
}

export const InfoBanner: React.FC<InfoBannerProps> = ({
  content,
  bannerType = "auto",
  ctaLink,
  ctaLabel,
  hideCta = false,
  hideCloseIcon = false,
  showInfoBanner = true,
  storageKey = "isShowInfoBanner",
  dataTestId = "info-banner",
  className = "",
}) => {
  const messageBus = getMessageBus();
  const { __ } = useTranslations();
  const bannerRef = useRef<HTMLDivElement>(null);
  const { getStorage: getSession, setStorage: setSession } = useSessionStorage();
  const [showBanner, setShowBanner] = useState<boolean>(false);

  useEffect(() => {
    if (!showInfoBanner) {
      setShowBanner(false);
      return;
    }

    try {
      const storedValue = getSession(storageKey, showInfoBanner);
      setShowBanner(storedValue);
    } catch (error) {
      console.error("Error reading from sessionStorage:", error);
      setShowBanner(showInfoBanner);
    }
  }, [showInfoBanner]);

  const updateBannerHeight = useCallback(() => {
    if (!bannerRef.current) return;
    const height = Math.round(bannerRef.current.getBoundingClientRect().height);
    document.documentElement.style.setProperty("--info-banner-height", `${height}px`);
  }, []);

  useEffect(() => {
    if (!showBanner || !bannerRef.current) return;
    updateBannerHeight();

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0 && entries[0].target === bannerRef.current) {
        updateBannerHeight();
      }
    });

    resizeObserver.observe(bannerRef.current);

    return () => {
      resizeObserver.disconnect();
      document.documentElement.style.removeProperty("--info-banner-height");
    };
  }, [showBanner, updateBannerHeight]);

  useEffect(() => {
    if (showBanner) {
      document.body.classList.add("body--info-banner");
      messageBus.publish("infobanner.active", {
        payload: {
          infoBannerHeight: Math.round(bannerRef?.current?.getBoundingClientRect()?.height ?? 0),
        },
      });

      return () => {
        document.body.classList.remove("body--info-banner");
      };
    }
  }, [showBanner]);

  const handleClose = useCallback(() => {
    setShowBanner(false);
    try {
      setSession(storageKey, false);
      messageBus.publish("infobanner.close", { payload: null });
    } catch (error) {
      console.error("Error writing to sessionStorage:", error);
    }
  }, [storageKey]);

  if (!showBanner) return null;

  const bannerClassName = classNames("info-banner", `info-banner--${bannerType}`, className, {
    "light-theme": bannerType !== "auto",
  });

  const dismissibleProps = !hideCloseIcon
    ? {
        isDismissible: true as const,
        dismissLabel: __("common.close"),
        onDismiss: handleClose,
      }
    : {};

  return (
    <div ref={bannerRef} className={bannerClassName} data-testid={dataTestId} aria-live="polite">
      <CoreInfoBanner
        tone={bannerTypeToTone[bannerType]}
        action={!hideCta ? { children: ctaLabel, href: ctaLink } : undefined}
        {...dismissibleProps}
      >
        {content}
      </CoreInfoBanner>
    </div>
  );
};
